import { useCallback, useState } from "react";

/**
 * ACT-1247 (Phase 4) — remount key + imperative refresh for the in-app
 * Provider Portal embed.
 *
 * The Authorizations module in `@astrana/ui` self-fetches internally and
 * exposes no `refetch`/`refresh` prop, so member-360 can't imperatively
 * re-run the query. Instead we bump a remount key: the consumer feeds
 * `refreshKey` into the `key` of the subtree wrapping `<MemberProfile>`, and
 * a changed key remounts the modules, which re-fetch on mount. `refreshNow()`
 * bumps it immediately — e.g. when the in-app embed modal reports
 * `PP_EMBED_AUTH_CREATED`, or when a claim is created — independent of any
 * browser event.
 *
 * REMOVED (AM-5828 handoff purge): this hook used to also arm a
 * `markHandoffPending` flag consumed by `window` `focus` /
 * `document` `visibilitychange` listeners, for a since-abandoned "open PP in
 * a new tab, detect the return via focus" handoff. Every call site had
 * already been removed (AM-5701, AM-5828), leaving the arm/listener/debounce
 * machinery as dead code that still fired on every tab focus — deleted here.
 */

export interface ReturnFocusRefresh {
  /** Monotonic key; feed into the `key` of the subtree to remount on refresh. */
  refreshKey: number;
  /** Refresh the subtree IMMEDIATELY (bump the key now). */
  refreshNow: () => void;
}

export function useReturnFocusRefresh(): ReturnFocusRefresh {
  const [refreshKey, setRefreshKey] = useState(0);

  const refreshNow = useCallback(() => {
    setRefreshKey((key) => key + 1);
  }, []);

  return { refreshKey, refreshNow };
}
